import { Deal } from "@/types/deal";
import { OpexState, OpexItem, OpexResults, Driver } from "../types/opex";

export type RevenueData = {
  totalRoomsRevenue: number;
  totalFnbRevenue: number;
  totalOtherRevenue: number;
  totalRevenue: number;
  roomsSoldByMonth: number[];
  roomsSoldYearTotal: number;
};

export function calculateOpexItem(
  item: OpexItem,
  revenueData: RevenueData
): number {
  const { value, driver } = item;
  const {
    totalRoomsRevenue,
    totalFnbRevenue,
    totalOtherRevenue,
    totalRevenue,
    roomsSoldYearTotal,
  } = revenueData;

  switch (driver) {
    case "PCT_ROOMS_REVENUE":
      return (value / 100) * totalRoomsRevenue;
    case "PCT_FNB_REVENUE":
      return (value / 100) * totalFnbRevenue;
    case "PCT_OTHER_REVENUE":
      return (value / 100) * totalOtherRevenue;
    case "PCT_TOTAL_REVENUE":
      return (value / 100) * totalRevenue;
    case "PER_ROOM_NIGHT_SOLD":
      return value * roomsSoldYearTotal;
    case "FIXED_PER_MONTH":
      return value * 12;
    default:
      return 0;
  }
}

export function calculateOpexResults(
  state: OpexState,
  revenueData: RevenueData
): OpexResults {
  const byItem: Record<string, number> = {};
  const bySection = { DIRECT: 0, INDIRECT: 0, OTHER: 0 };

  state.items.forEach((item) => {
    const amount = calculateOpexItem(item, revenueData);
    byItem[item.id] = amount;
    bySection[item.section] += amount;
  });

  const directTotal = bySection.DIRECT;
  const indirectTotal = bySection.INDIRECT;
  const otherTotal = bySection.OTHER;
  const grandTotal = directTotal + indirectTotal + otherTotal;

  return {
    directTotal,
    indirectTotal,
    otherTotal,
    grandTotal,
    byItem,
    bySection,
  };
}

export function getDriverLabel(driver: Driver): string {
  switch (driver) {
    case "PCT_ROOMS_REVENUE":
      return "% of Rooms Revenue";
    case "PCT_FNB_REVENUE":
      return "% of F&B Revenue";
    case "PCT_OTHER_REVENUE":
      return "% of Other Revenue";
    case "PCT_TOTAL_REVENUE":
      return "% of Total Revenue";
    case "PER_ROOM_NIGHT_SOLD":
      return `per Room Night Sold`;
    case "FIXED_PER_MONTH":
      return `Fixed per Month`;
    default:
      return "Unknown";
  }
}
export const calculateYear3Amount = (
  item: any,
  deal: Deal,
  revenueData: RevenueData
) => {
  if (!deal || !item) return 0;

  const targetYear = 3;

  // Получаем настройки ramp
  const rampSettings =
    deal.kpis?.rampSettings || deal.assumptions?.rampSettings;
  const rampKpis = deal.kpis?.ramp;

  // Ramp множители для каждого года
  const revenueRamp = rampSettings?.revenueRamp || [1, 1, 1, 1];
  const costRamp = rampSettings?.costRamp || [1, 1, 1, 1];

  // Рост и инфляция
  const inflationPct =
    rampSettings?.inflationPct || rampKpis?.inflationRate || 2;
  const toplineGrowthPct =
    rampSettings?.toplineGrowthPct || rampKpis?.revenueGrowthY3Plus || 2;

  const inflationRate = inflationPct / 100;
  const growthRate = toplineGrowthPct / 100;

  // Базовое значение (вводимое пользователем)
  const baseValue = item.value || 0;

  // Получаем базовую выручку для Year 1
  const baseRevenue = revenueData.totalRevenue || 0;
  const baseRoomsRevenue = revenueData.totalRoomsRevenue || 0;
  const baseFnbRevenue = revenueData.totalFnbRevenue || 0;
  const baseRoomsSold = revenueData.roomsSoldYearTotal || 0;

  // Рассчитываем значения для Year 1 с учетом ramp
  const year1Revenue = baseRevenue * (revenueRamp[0] || 1);
  const year1RoomsRevenue = baseRoomsRevenue * (revenueRamp[0] || 1);
  const year1FnbRevenue = baseFnbRevenue * (revenueRamp[0] || 1);
  const year1RoomsSold = baseRoomsSold * (revenueRamp[0] || 1);

  // Рассчитываем для Year 3
  // Для роста: Year 1 → Year 2 → Year 3 (2 года роста)
  const yearsGrowth = Math.pow(1 + growthRate, 2);
  // Для инфляции: Year 1 → Year 2 → Year 3 (2 года инфляции)
  const yearsInflation = Math.pow(1 + inflationRate, 2);

  switch (item.driver) {
    case "percentRevenue":
      // % от общей выручки
      // Year 3 Revenue = Year 1 Revenue * рост на 2 года
      const year3Revenue = year1Revenue * yearsGrowth;
      // Year 3 Cost = Year 3 Revenue * процент * costRamp для Year 3 * инфляция
      return (
        year3Revenue * (baseValue / 100) * (costRamp[2] || 1) * yearsInflation
      );

    case "percentRoomsRevenue":
      // % от выручки номеров
      const year3RoomsRevenue = year1RoomsRevenue * yearsGrowth;
      return (
        year3RoomsRevenue *
        (baseValue / 100) *
        (costRamp[2] || 1) *
        yearsInflation
      );

    case "perRoomNight":
      // За проданную ночь
      const year3RoomsSold = year1RoomsSold * yearsGrowth;
      return year3RoomsSold * baseValue * (costRamp[2] || 1) * yearsInflation;

    case "monthlyFixed":
      // Ежемесячно фиксировано
      const monthlyBase = baseValue * 12; // Year 1 годовая
      const year3Monthly = monthlyBase * (costRamp[2] || 1) * yearsInflation;
      return year3Monthly;

    case "percentFnbRevenue":
      // % от F&B выручки
      const year3FnbRevenue = year1FnbRevenue * yearsGrowth;
      return (
        year3FnbRevenue *
        (baseValue / 100) *
        (costRamp[2] || 1) *
        yearsInflation
      );

    case "revenue":
      // Общий случай для выручки (используется для rooms-commission)
      const revenueDriverValue = year1Revenue * yearsGrowth;
      return (
        revenueDriverValue *
        (baseValue / 100) *
        (costRamp[2] || 1) *
        yearsInflation
      );

    default:
      // Для фиксированных затрат или других драйверов
      const year1Cost = baseValue;
      return year1Cost * (costRamp[2] || 1) * yearsInflation;
  }
};
